import { setCurrency } from '../lib/currency';
/**
 * TenantContext (iter308d, Phase 5a)
 *
 * Loads the current tenant's branding (logo, colors, business name,
 * contact info, social links, hero copy) BEFORE any auth check.
 *
 * Why pre-auth?
 * The landing & login pages need to render the right tenant's brand
 * identity even when the visitor has no session — typing
 * dasbakinacademy.uk in their browser should show *that* tenant's
 * logo and business name, not the platform default.
 *
 * How it works
 * - On mount we GET /api/public/branding (no auth). The backend resolves
 *   the tenant from the Host header via the existing tenancy middleware.
 * - We dump the branding into React context and ALSO into:
 *     - `document.title` (the browser tab title)
 *     - `<link rel="icon">` favicon
 *     - CSS custom properties on `:root`:
 *        --brand-primary, --brand-accent
 *   This lets non-React code (e.g. landing page hero CSS) consume the
 *   branding too.
 * - Persisted to sessionStorage so subsequent navigations within the
 *   same SPA session are instant. Re-fetched on full page reloads.
 */
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const STORAGE_KEY = 'tenant-branding-v2';

/**
 * Convert a #RRGGBB / #RGB hex colour to a "H S% L%" string suitable for
 * dropping into a CSS custom property that shadcn consumes via `hsl(var(...))`.
 * Returns null on invalid input so the caller can leave the existing
 * variable untouched.
 */
function hexToHslTriplet(hex) {
  if (typeof hex !== 'string') return null;
  let h = hex.trim();
  if (h.startsWith('#')) h = h.slice(1);
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (h.length !== 6) return null;
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let H = 0;
  let S;
  const L = (max + min) / 2;
  if (max === min) {
    H = 0;
    S = 0;
  } else {
    const d = max - min;
    S = L > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: H = (g - b) / d + (g < b ? 6 : 0); break;
      case g: H = (b - r) / d + 2; break;
      case b: H = (r - g) / d + 4; break;
      default: break;
    }
    H /= 6;
  }
  return `${Math.round(H * 360)} ${Math.round(S * 100)}% ${Math.round(L * 100)}%`;
}

const FALLBACK_BRANDING = {
  is_platform: false,
  tenant_id: null,
  tenant_slug: null,
  tenant_name: null,
  business_name: 'Cricket Academy',
  business_tagline: null,
  primary_color: '#F97316',
  accent_color: '#0F172A',
  logo_url: null,
  logo_id: null,
  logo_size: 'medium',
  logo_alignment: 'left',
  background_url: null,
  background_opacity: 0.5,
  favicon_url: null,
  business_phone: null,
  whatsapp_number: null,
  website: null,
  address: null,
  support_email: null,
  social_links: {},
  hero_copy: null,
  maintenance_mode: false,
  maintenance_message: null,
};

const TenantContext = createContext({
  branding: FALLBACK_BRANDING,
  loading: true,
  refresh: () => {},
});

const apiBase = (typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL);

function _applyBrandingToDocument(b) {
  try {
    // Tab title — keep platform name when no tenant override exists.
    if (b?.business_name) {
      document.title = b.business_name;
    }
    const root = document.documentElement;
    // Legacy single-colour CSS variables (kept for backwards-compat with
    // pages that still read --brand-primary directly).
    if (b?.primary_color) root.style.setProperty('--brand-primary', b.primary_color);
    if (b?.accent_color) root.style.setProperty('--brand-accent', b.accent_color);
    // iter313 — Full theme palette → CSS custom properties.
    // Mapped both as semantic vars (--brand-*) and Tailwind-token
    // friendly vars (--theme-*) so tailwind.config.js can reference
    // them via `theme.colors.brand.primary` → `var(--theme-primary)`.
    const palette = b?.theme?.palette || null;
    if (palette) {
      const map = {
        primary: '--theme-primary',
        primary_fg: '--theme-primary-fg',
        secondary: '--theme-secondary',
        accent: '--theme-accent',
        bg: '--theme-bg',
        surface: '--theme-surface',
        text: '--theme-text',
        muted: '--theme-muted',
        success: '--theme-success',
        warn: '--theme-warn',
        error: '--theme-error',
      };
      Object.entries(map).forEach(([k, cssVar]) => {
        if (palette[k]) root.style.setProperty(cssVar, palette[k]);
      });
      // iter314 — Drive shadcn's HSL-formatted variables from the same
      // palette so Button/Card/Badge/Input/Tabs (which all consume
      // `hsl(var(--primary))` etc. in tailwind.config) auto-theme. We
      // map sensibly:
      //   palette.primary       → --primary  (action colour)
      //   palette.primary_fg    → --primary-foreground
      //   palette.secondary     → --secondary
      //   palette.bg            → --background
      //   palette.surface       → --card / --popover
      //   palette.text          → --foreground / --card-foreground
      //   palette.muted         → --muted-foreground / --border
      //   palette.accent        → --accent
      //   palette.error         → --destructive
      const shadcnMap = {
        primary: '--primary',
        primary_fg: '--primary-foreground',
        secondary: '--secondary',
        bg: '--background',
        surface: '--card',
        text: '--foreground',
        muted: '--muted-foreground',
        accent: '--accent',
        error: '--destructive',
      };
      Object.entries(shadcnMap).forEach(([paletteKey, cssVar]) => {
        const hsl = hexToHslTriplet(palette[paletteKey]);
        if (hsl) root.style.setProperty(cssVar, hsl);
      });
      // Also drive the more granular shadcn slots so cards & popovers
      // line up with the chosen palette.
      const surfaceHsl = hexToHslTriplet(palette.surface);
      if (surfaceHsl) {
        root.style.setProperty('--popover', surfaceHsl);
      }
      const textHsl = hexToHslTriplet(palette.text);
      if (textHsl) {
        root.style.setProperty('--card-foreground', textHsl);
        root.style.setProperty('--popover-foreground', textHsl);
        root.style.setProperty('--secondary-foreground', textHsl);
        root.style.setProperty('--accent-foreground', textHsl);
      }
      // Ring colour follows the primary so focus outlines match.
      const primaryHsl = hexToHslTriplet(palette.primary);
      if (primaryHsl) root.style.setProperty('--ring', primaryHsl);
      // Border = muted, input = muted (consistent with shadcn defaults).
      const mutedHsl = hexToHslTriplet(palette.muted);
      if (mutedHsl) {
        root.style.setProperty('--border', mutedHsl);
        root.style.setProperty('--input', mutedHsl);
        root.style.setProperty('--muted', hexToHslTriplet(palette.surface) || mutedHsl);
      }
      // Also tag the body so we can switch between light/dark text in
      // edge cases via `body[data-theme-bg='dark']`.
      const bg = (palette.bg || '').toLowerCase();
      const isDark = /^#([0-1][0-9a-f]{5}|[0-3][0-9a-f]|[0-3][0-9a-f]{2})$/i.test(bg);
      document.body.setAttribute('data-theme-bg', isDark ? 'dark' : 'light');
    }
    // Favicon — swap if tenant provided one OR if we're on the
    // platform host and a platform-level favicon is configured
    // (iter312). Tenant favicon takes priority over the platform
    // favicon so tenant hosts always show the academy's icon.
    const setFavicon = (href) => {
      let link = document.querySelector("link[rel*='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = href;
    };
    if (b?.favicon_url) {
      setFavicon(`${apiBase}${b.favicon_url}`);
    } else if (b?.is_platform && b?.platform_favicon_url) {
      setFavicon(`${apiBase}${b.platform_favicon_url}`);
    }
  } catch (_e) {
    // never break the SPA over a styling concern
  }
}

function _readCached() {
  try {
    // iter308v — Aggressively flush legacy cache buckets. v1 cached
    // tenant branding for cross-origin visits during the multi-tenant
    // cutover (esp. before iter308u shipped same-origin API), causing
    // platform / new-tenant hosts to render Delhi Capitals colors
    // for a flash before the fresh fetch resolved.
    try {
      sessionStorage.removeItem('tenant-branding-v1');
      sessionStorage.removeItem('tenant-branding');
    } catch (_e) { /* ignore */ }
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Drop the cache if it was written for a different hostname
    // (defensive — sessionStorage is per-origin, but redirects /
    // proxies sometimes blur this).
    if (typeof window !== 'undefined' && parsed && parsed._host && parsed._host !== window.location.hostname) {
      return null;
    }
    return parsed;
  } catch (_) {
    return null;
  }
}

export function TenantProvider({ children }) {
  const cached = _readCached();
  const [branding, setBranding] = useState(cached || FALLBACK_BRANDING);
  // Money symbols everywhere read the tenant currency from lib/currency.
  setCurrency(branding?.currency);
  const [loading, setLoading] = useState(!cached);

  const fetchBranding = async () => {
    try {
      // iter318 wave 13 — Preview override. Allows QA / stakeholders
      // to verify the vyomos_marketing shell on any preview URL by
      // appending `?_vyomos_intent=marketing`. Never sent to the API.
      if (typeof window !== 'undefined') {
        const qp = new URLSearchParams(window.location.search).get('_vyomos_intent');
        if (qp === 'marketing') {
          const merged = {
            ...FALLBACK_BRANDING,
            is_platform: true,
            route_intent: 'vyomos_marketing',
            platform_name: 'VyomOS',
            _host: window.location.hostname,
          };
          setBranding(merged);
          sessionStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
          _applyBrandingToDocument(merged);
          setLoading(false);
          return;
        }
      }
      const { data } = await axios.get(`${apiBase}/api/public/branding`, {
        withCredentials: false,
      });
      const merged = { ...FALLBACK_BRANDING, ...data };
      // White-label: <slug>.vyomos.io forwards to the academy's own domain (same path).
      // canonical_hostname (Super Admin → tenant) wins, e.g. www.<domain> when the bare domain has no DNS.
      const forwardHost = data?.canonical_hostname || data?.custom_domain;
      if (typeof window !== 'undefined' && forwardHost) {
        const h = window.location.hostname.toLowerCase();
        const cd = String(forwardHost).toLowerCase();
        const label = h.endsWith('.vyomos.io') ? h.slice(0, -'.vyomos.io'.length) : '';
        if (label && !label.includes('.') && !['www', 'my'].includes(label) && h !== cd) {
          const { pathname, search, hash } = window.location;
          window.location.replace(`https://${cd}${pathname}${search}${hash}`);
          return;
        }
      }
      // iter308v — stamp the hostname into the cached blob so a future
      // read on a different host correctly invalidates it.
      if (typeof window !== 'undefined') {
        merged._host = window.location.hostname;
      }
      setBranding(merged);
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
      _applyBrandingToDocument(merged);
    } catch (_err) {
      // Network failure or backend unreachable — fall back silently.
      // The cached value (if any) or FALLBACK_BRANDING continues to drive
      // the UI so the user still sees a usable login page.
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (cached) {
      // Cached value already applied on render — still hit the network
      // in the background so any branding change made by the Super
      // Admin shows up on next mount without a hard refresh.
      _applyBrandingToDocument(cached);
    }
    fetchBranding();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(
    () => ({ branding, loading, refresh: fetchBranding }),
    [branding, loading]
  );
  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant() {
  return useContext(TenantContext);
}
